import { memo, useCallback, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  flexRender,
  type ColumnDef,
} from '@tanstack/react-table';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Group,
  Loader,
  Menu,
  Select,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import {
  ChevronLeft,
  ChevronRight,
  Circle,
  Eye,
  MoreVertical,
} from 'lucide-react';
import type { PatientHubListRow } from '../types';
import {
  formatAgeValue,
  formatBiologicalSex,
  formatDateValue,
} from '../utils/formatters';
import { deriveAgeFromDateOfBirth } from '../utils/nutrition';
import { PATIENT_SESSION_STATUS_LABELS } from '../utils/sessions';

interface PatientsListProps {
  data: PatientHubListRow[];
  loading: boolean;
  totalRecords: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  hasActiveFilters?: boolean;
  deletingPatientId?: number | null;
  isMobile?: boolean;
  isTablet?: boolean;
  onView: (patient: PatientHubListRow) => void;
  onEdit: (patient: PatientHubListRow) => void;
  onDelete: (patient: PatientHubListRow) => void;
}

const MeasurementsBadge = memo<{ hasBodyMeasurements: boolean }>(
  ({ hasBodyMeasurements }) => (
    <Badge
      color={hasBodyMeasurements ? 'green' : 'gray'}
      variant='light'
      size='sm'
      leftSection={
        <Circle
          size={6}
          fill={
            hasBodyMeasurements
              ? 'var(--mantine-color-green-6)'
              : 'var(--mantine-color-gray-6)'
          }
          color={
            hasBodyMeasurements
              ? 'var(--mantine-color-green-6)'
              : 'var(--mantine-color-gray-6)'
          }
        />
      }
    >
      {hasBodyMeasurements ? 'Disponibili' : 'Non disponibili'}
    </Badge>
  )
);

MeasurementsBadge.displayName = 'MeasurementsBadge';

const ActionsCell = memo<{
  patient: PatientHubListRow;
  deletingPatientId?: number | null;
  onView: (patient: PatientHubListRow) => void;
  onEdit: (patient: PatientHubListRow) => void;
  onDelete: (patient: PatientHubListRow) => void;
}>(({ patient, deletingPatientId, onView, onEdit, onDelete }) => {
  const handleView = useCallback(() => {
    onView(patient);
  }, [onView, patient]);

  const handleEdit = useCallback(() => {
    onEdit(patient);
  }, [onEdit, patient]);

  const handleDelete = useCallback(() => {
    onDelete(patient);
  }, [onDelete, patient]);

  return (
    <Group gap='xs'>
      <Button
        variant='subtle'
        size='sm'
        leftSection={<Eye size={16} />}
        onClick={handleView}
      >
        Apri
      </Button>
      <Menu position='bottom-end' withArrow>
        <Menu.Target>
          <ActionIcon
            variant='subtle'
            size='sm'
            color='gray'
            aria-label={`Altre azioni per ${patient.code}`}
            loading={deletingPatientId === patient.id}
            disabled={deletingPatientId === patient.id}
          >
            <MoreVertical size={16} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item onClick={handleEdit}>Modifica</Menu.Item>
          <Menu.Item
            color='red'
            onClick={handleDelete}
            disabled={deletingPatientId === patient.id}
          >
            Elimina
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
});

ActionsCell.displayName = 'ActionsCell';

const PatientsPagination = memo<{
  totalRecords: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}>(({ totalRecords, page, pageSize, onPageChange, onPageSizeChange }) => {
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalRecords / pageSize)),
    [totalRecords, pageSize]
  );

  const startRecord = useMemo(
    () => (totalRecords === 0 ? 0 : (page - 1) * pageSize + 1),
    [page, pageSize, totalRecords]
  );

  const endRecord = useMemo(
    () => Math.min(page * pageSize, totalRecords),
    [page, pageSize, totalRecords]
  );

  const pageNumbers = useMemo(() => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else if (page <= 3) {
      pages.push(1, 2, 3, 4, '...', totalPages);
    } else if (page >= totalPages - 2) {
      pages.push(
        1,
        '...',
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages
      );
    } else {
      pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
    }

    return pages;
  }, [page, totalPages]);

  return (
    <Flex justify='space-between' align='center' mt='md' wrap='wrap' gap='md'>
      <Group gap='sm' align='center'>
        <Text size='sm' c='dimmed'>
          Mostrati {startRecord}-{endRecord} utenti su {totalRecords}
        </Text>
        <Select
          value={pageSize.toString()}
          onChange={value => {
            if (!value) return;
            onPageSizeChange(Number(value));
            onPageChange(1);
          }}
          data={[
            { value: '10', label: '10' },
            { value: '20', label: '20' },
            { value: '50', label: '50' },
            { value: '100', label: '100' },
          ]}
          size='sm'
          w={80}
          radius='sm'
        />
      </Group>
      <Group gap='xs'>
        <Button
          variant='outline'
          size='sm'
          radius='sm'
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
          leftSection={<ChevronLeft size={16} />}
        >
          Previous
        </Button>
        {pageNumbers.map((pageNum, index) =>
          pageNum === '...' ? (
            <Text key={`ellipsis-${index}`} size='sm' c='dimmed' px='xs'>
              ...
            </Text>
          ) : (
            <Button
              key={pageNum}
              variant={pageNum === page ? 'filled' : 'subtle'}
              color={pageNum === page ? 'blue' : 'gray'}
              size='sm'
              radius='sm'
              onClick={() => onPageChange(pageNum as number)}
            >
              {pageNum}
            </Button>
          )
        )}
        <Button
          variant='filled'
          color='blue'
          size='sm'
          radius='sm'
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          rightSection={<ChevronRight size={16} />}
        >
          Avanti
        </Button>
      </Group>
    </Flex>
  );
});

PatientsPagination.displayName = 'PatientsPagination';

export function PatientsList({
  data,
  loading,
  totalRecords,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  hasActiveFilters = false,
  deletingPatientId = null,
  isMobile = false,
  isTablet = false,
  onView,
  onEdit,
  onDelete,
}: PatientsListProps) {
  const columnHelper = createColumnHelper<PatientHubListRow>();

  const columns = useMemo<ColumnDef<PatientHubListRow>[]>(() => {
    const baseColumns: ColumnDef<PatientHubListRow>[] = [
      columnHelper.display({
        id: 'user',
        header: 'Utente',
        cell: ({ row }) => {
          const patient = row.original;
          return (
            <Stack gap={2}>
              <Text size='sm' fw={500}>
                {patient.code}
              </Text>
              <Text size='xs' c='dimmed'>
                ID #{patient.id}
              </Text>
            </Stack>
          );
        },
      }),
      columnHelper.display({
        id: 'gender',
        header: 'Sesso',
        cell: ({ row }) => (
          <Text size='sm' fw={500}>
            {formatBiologicalSex(row.original.profile.biologicalSex)}
          </Text>
        ),
      }),
      columnHelper.display({
        id: 'age',
        header: 'Età',
        cell: ({ row }) => (
          <Text size='sm' fw={500}>
            {formatAgeValue(
              deriveAgeFromDateOfBirth(row.original.profile.dateOfBirth)
            )}
          </Text>
        ),
      }),
      columnHelper.display({
        id: 'measurements',
        header: 'Rilevazioni corporee',
        minSize: 160,
        size: 160,
        cell: ({ row }) => (
          <MeasurementsBadge
            hasBodyMeasurements={row.original.hasBodyMeasurements}
          />
        ),
      }),
      columnHelper.display({
        id: 'activeSession',
        header: 'Ultima sessione attiva',
        minSize: 180,
        size: 180,
        cell: ({ row }) => {
          const session = row.original.latestActiveSession;
          if (session == null) {
            return (
              <Text size='sm' c='dimmed'>
                —
              </Text>
            );
          }

          const statusLabel =
            PATIENT_SESSION_STATUS_LABELS[session.status] ?? session.status;
          const referenceDate =
            session.updatedAt ?? session.startedAt ?? session.createdAt;

          return (
            <Stack gap={2}>
              <Text size='sm' fw={500}>
                #{session.id} · {statusLabel}
              </Text>
              <Text size='xs' c='dimmed'>
                {formatDateValue(referenceDate)}
              </Text>
            </Stack>
          );
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Azioni',
        minSize: 100,
        size: 100,
        cell: ({ row }) => (
          <ActionsCell
            patient={row.original}
            deletingPatientId={deletingPatientId}
            onView={onView}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ),
      }),
    ];

    if (isMobile) {
      return baseColumns.filter(
        column => column.id === 'user' || column.id === 'actions'
      );
    }

    if (isTablet) {
      return baseColumns.filter(
        column => column.id !== 'gender' && column.id !== 'age'
      );
    }

    return baseColumns;
  }, [
    columnHelper,
    deletingPatientId,
    isMobile,
    isTablet,
    onDelete,
    onEdit,
    onView,
  ]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (loading) {
    return (
      <Box ta='center' py='xl'>
        <Loader size='md' />
      </Box>
    );
  }

  if (totalRecords === 0) {
    return (
      <Card shadow='sm' radius='sm' withBorder p='md'>
        <Stack gap='md'>
          <Box>
            <Text size='sm' fw={600} mb={4}>
              Utenti
            </Text>
            <Text size='xs' c='dimmed'>
              Visualizza e gestisci tutti gli utenti della tua rubrica
            </Text>
          </Box>
          <Box ta='center' py='xl'>
            <Stack gap='md' align='center'>
              <Text size='sm' c='dimmed'>
                {hasActiveFilters
                  ? 'Nessun utente corrisponde ai filtri selezionati'
                  : 'Nessun utente disponibile'}
              </Text>
              {hasActiveFilters ? (
                <Text size='xs' c='dimmed'>
                  Prova a cambiare la ricerca o i filtri selezionati.
                </Text>
              ) : null}
            </Stack>
          </Box>
        </Stack>
      </Card>
    );
  }

  return (
    <Card shadow='sm' radius='sm' withBorder p='md'>
      <Stack gap='md'>
        <Box>
          <Text size='sm' fw={600} mb={4}>
            Utenti
          </Text>
          <Text size='xs' c='dimmed'>
            Visualizza e gestisci tutti gli utenti della tua rubrica
          </Text>
        </Box>
        <Table
          verticalSpacing='xs'
          horizontalSpacing='sm'
          highlightOnHover
          withTableBorder
        >
          <Table.Thead>
            {table.getHeaderGroups().map(headerGroup => (
              <Table.Tr key={headerGroup.id} bg='var(--mantine-color-gray-0)'>
                {headerGroup.headers.map(header => (
                  <Table.Th key={header.id} p='sm' fw={500}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </Table.Th>
                ))}
              </Table.Tr>
            ))}
          </Table.Thead>
          <Table.Tbody>
            {table.getRowModel().rows.map(row => (
              <Table.Tr key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <Table.Td key={cell.id} p='sm'>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        <PatientsPagination
          totalRecords={totalRecords}
          page={page}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      </Stack>
    </Card>
  );
}
